import React, { useContext } from 'react';
import { CRMContext } from '../context/CRMContext';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiCheckCircle, FiClock, FiCalendar } from 'react-icons/fi';
import { format, isToday, isPast } from 'date-fns';

const Dashboard = () => {
  const { leads, events } = useContext(CRMContext);
  const pendingEvents = events.filter(e => e.status === 'pending');
  
  const upcomingEvents = pendingEvents
    .filter(e => !isPast(new Date(e.date)) || isToday(new Date(e.date)))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);

  const overdueEvents = pendingEvents.filter(e => isPast(new Date(e.date)) && !isToday(new Date(e.date)));

  const stats = [
    { label: 'Total de Leads', value: leads.length, icon: FiUsers, color: '#2563EB', bg: '#EFF6FF' },
    { label: 'Tareas Pendientes', value: pendingEvents.filter(e => e.type === 'task').length, icon: FiClock, color: '#F59E0B', bg: '#FFFBEB' },
    { label: 'Próximas Citas', value: pendingEvents.filter(e => e.type === 'appointment').length, icon: FiCalendar, color: '#14B8A6', bg: '#F0FDFA' },
    { label: 'Vencidos', value: overdueEvents.length, icon: FiCheckCircle, color: '#EF4444', bg: '#FEF2F2' },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Resumen del Panel</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-xl flex items-center gap-4" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div className="p-4 rounded-xl flex-shrink-0" style={{ backgroundColor: stat.bg }}>
              <SafeIcon icon={stat.icon} className="text-2xl" style={{ color: stat.color }} />
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: '#64748B' }}>{stat.label}</p>
              <p className="text-2xl font-bold" style={{ color: '#0F172A' }}>{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming */}
        <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="p-4 flex justify-between items-center" style={{ borderBottom: '1px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
            <h2 className="text-base font-bold" style={{ color: '#0F172A' }}>Próximas Tareas</h2>
            <Link to="/calendar" className="text-sm font-semibold hover:underline" style={{ color: '#2563EB' }}>Ver Calendario</Link>
          </div>
          <div className="divide-y" style={{ borderColor: '#F1F5F9' }}>
            {upcomingEvents.length === 0 ? (
              <p className="p-6 text-center text-sm italic" style={{ color: '#94A3B8' }}>Sin eventos próximos.</p>
            ) : upcomingEvents.map(event => {
              const lead = leads.find(l => l.id === event.leadId);
              return (
                <div key={event.id} className="p-4 flex items-start gap-3" style={{ borderColor: '#F1F5F9' }}>
                  <div className="mt-1 p-2 rounded-lg flex-shrink-0" style={event.type === 'task' ? { backgroundColor: '#FFFBEB', color: '#F59E0B' } : { backgroundColor: '#F0FDFA', color: '#14B8A6' }}>
                    <SafeIcon icon={event.type === 'task' ? FiClock : FiCalendar} className="text-sm" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: '#0F172A' }}>{event.title}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>{lead?.name} · {format(new Date(event.date), 'd MMM, h:mm a')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Leads */}
        <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div className="p-4 flex justify-between items-center" style={{ borderBottom: '1px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
            <h2 className="text-base font-bold" style={{ color: '#0F172A' }}>Leads Recientes</h2>
            <Link to="/leads" className="text-sm font-semibold hover:underline" style={{ color: '#2563EB' }}>Ver Todos</Link>
          </div>
          <div className="divide-y" style={{ borderColor: '#F1F5F9' }}>
            {leads.slice(0, 5).map(lead => (
              <Link key={lead.id} to={`/leads/${lead.id}`} className="p-4 flex items-center justify-between group block" style={{ borderColor: '#F1F5F9' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div>
                  <p className="font-semibold text-sm transition-colors" style={{ color: '#0F172A' }}>{lead.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>{lead.company}</p>
                </div>
                <StatusBadge status={lead.status} />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    New:       { backgroundColor: '#EFF6FF', color: '#2563EB', label: 'Nuevo' },
    Contacted: { backgroundColor: '#F0F9FF', color: '#0284C7', label: 'Contactado' },
    Qualified: { backgroundColor: '#F0FDF4', color: '#16A34A', label: 'Calificado' },
    Won:       { backgroundColor: '#FFFBEB', color: '#D97706', label: 'Ganado' },
    Lost:      { backgroundColor: '#F1F5F9', color: '#64748B', label: 'Perdido' },
  };
  const config = styles[status] || styles.Lost;
  return (
    <span className="px-2.5 py-1 text-xs font-bold rounded-full uppercase tracking-wide" style={{ backgroundColor: config.backgroundColor, color: config.color }}>
      {config.label}
    </span>
  );
};

export default Dashboard;